import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Link } from 'react-router-dom';
import { Button, Card, Table } from 'antd';
import { EmptyState } from '../empty_state/EmptyState';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import OWButtonWithSupplierManagedLock from '../common/OWButtonWithSupplierManagedLock';
import { isThirdPartySupplier, PERMISSION_NAMES } from '../../utils/AuthUtils';
import {
	getEntityById,
	nullSafeGet,
	nullSafeGetOrElse,
	nullSafeGetWithZero,
} from '../../utils/DataAccessUtils';
import { dateFormatter, DATE_ONLY_FORMAT } from '../../utils/DataFormatterUtils';
import { HorizontalKeyValueDisplay } from '../horizontal_key_value_display/HorizontalKeyValueDisplay';
import { withUserType } from '../withUserType';
import {
	formatGwpAndCo2e,
	formatLeakRatePercent,
	formatPounds,
	getApplianceCategoryLabel,
	REFRIGERANT_ASSET_LEDGER_TC,
} from '../../utils/RefrigerantUtils';
import {
	refrigerantEventsRestCrudThunks,
	retrieveRefrigerantAssetSummary,
} from '../../thunks/refrigerant_events_thunks';
import RefrigerantBandDisplay from '../refrigerant/RefrigerantBandDisplay';
import RefrigerantLeakRateBar from '../refrigerant/RefrigerantLeakRateBar';
import LogRefrigerantEventDrawer from '../refrigerant/LogRefrigerantEventDrawer';
import getRefrigerantEventLedgerColumns, {
	getRefrigerantEventLedgerMinTableWidth,
} from '../refrigerant/RefrigerantEventLedgerColumns';
import { getLinkWIthBackLinkParams } from '../../utils/HistoryUtils';
import { getRecordsForTargetCollection } from '../../reducers/standard_reducer_utils';

// The whole ledger for one appliance, newest first. Not paged: a 608 servicing record is read as a
// whole — an auditor asks "what happened to this appliance", not "what happened on page 2" — and an
// appliance accumulates a handful of events a year, not thousands.
const LEDGER_PAGE_SIZE = 200;

/**
 * The appliance's own compliance record: the derived summary on top, the append-only ledger below.
 *
 * The summary comes from `asset_summary`, which the server computes from MySQL rather than reading
 * the OpenSearch projection — so it is exact and always fresh, and it is the authoritative number
 * when it and the index screen disagree. It also carries `thresholdPercent`, which the projection
 * does not, so this screen never has to re-derive a regulated threshold from config.
 *
 * Mounted two ways. As the asset detail page's own tab it takes the appliance from the route. Embedded
 * in a work order's Refrigerant Tracking tab it is handed `assetId` / `asset` as props, because there
 * `match.params.id` is the WORK ORDER's id — reading it would silently load the summary and ledger of
 * whichever appliance happens to share that number. `workOrder`, when present, is threaded to the log
 * drawer so the event a tech records from inside a work order is attributed to it.
 */
const AssetRefrigerantTrackingPage: FC<any> = ({
	asset,
	assetId: assetIdFromProps,
	workOrder,
	match,
	location,
	userType,
	currentUser,
	events,
	fetchSummary,
	fetchEvents,
}) => {
	const assetId = assetIdFromProps || nullSafeGet('params.id', match);
	const [summary, setSummary] = useState(null);
	const [loading, setLoading] = useState(true);
	const [logEventVisible, setLogEventVisible] = useState(false);
	// Distinguishes "the reads failed" from "this appliance has nothing recorded". Both used to render
	// the same all-dashes card, which on a regulated leak rate is the one confusion that matters.
	const [loadFailed, setLoadFailed] = useState(false);

	// Which appliance the state below belongs to. `summary` is a regulated figure that is also handed
	// to the log drawer, so it must never outlive the asset it was fetched for: when the route (or the
	// work order's appliance selector) switches `assetId`, the previous summary is dropped during
	// render rather than left on screen until the new request lands.
	const [summaryAssetId, setSummaryAssetId] = useState(assetId);
	if (summaryAssetId !== assetId) {
		setSummaryAssetId(assetId);
		setSummary(null);
		setLoadFailed(false);
	}

	// Bumped on every refresh so an in-flight request can tell whether it is still the current one.
	// Without this the LOSER of a race writes last: switching appliances while the first summary is
	// still open would leave the previous appliance's leak rate on screen indefinitely, and pass it to
	// the drawer as the newly selected appliance's compliance record.
	const requestRef = useRef(0);

	const refresh = useCallback(() => {
		const requestId = requestRef.current + 1;
		requestRef.current = requestId;
		setLoading(true);
		setLoadFailed(false);
		return (
			Promise.all([
				fetchSummary(userType, assetId).then((result) => {
					if (requestRef.current === requestId) setSummary(result);
				}),
				fetchEvents(userType, assetId),
			])
				// `.finally` alone does not consume a rejection. `retrieveRefrigerantAssetSummary` rejects on
				// 401 and on any network/parse failure, and the generated read rejects on an error envelope —
				// so without this the promise rejected unhandled AND the screen fell through to its normal
				// render, where every figure resolves to `--` and the ledger shows its empty state. A reader
				// cannot tell that from an appliance that genuinely has no events, which is the worst possible
				// ambiguity on a compliance record.
				.catch(() => {
					if (requestRef.current === requestId) setLoadFailed(true);
				})
				.finally(() => {
					if (requestRef.current === requestId) setLoading(false);
				})
		);
	}, [assetId, fetchEvents, fetchSummary, userType]);

	useEffect(() => {
		refresh();
	}, [refresh]);

	const ledger = useMemo(
		() => getRecordsForTargetCollection(events, REFRIGERANT_ASSET_LEDGER_TC),
		[events]
	);

	const columns = useMemo(
		// Every row is this appliance, so the Asset column would repeat the page title on every line.
		() => getRefrigerantEventLedgerColumns({ userType, showAsset: false }),
		[userType]
	);

	// Memoised alongside the columns it is derived from: built inline it would hand antd's Table a new
	// `scroll` object on every render, which is prop churn on the one component here that re-measures
	// when its props change.
	const ledgerScroll = useMemo(
		() => ({ x: getRefrigerantEventLedgerMinTableWidth(columns) }),
		[columns]
	);

	// A third-party supplier's ledger reads FAIL CLOSED onto an empty list rather than erroring (see
	// refrigerant_events_thunks) — the server will not hand another company's 608 history to a
	// contractor. An empty ledger is a safe thing to render as a table; it is NOT a safe thing to
	// render as a printable servicing record, because that document then attests that no servicing was
	// performed on an appliance whose real ledger the reader simply cannot see. Withhold the record
	// rather than let a contractor generate a compliance artifact stating the opposite of the truth.
	const canReadLedger = !isThirdPartySupplier(currentUser);

	// The write side of the same rule. Named separately from `canReadLedger` because they answer
	// different questions even though today's condition is the same one.
	const canLogEvent = canReadLedger;

	const leakRatePercent = nullSafeGetWithZero('leakRatePercent', summary);
	const thresholdPercent = nullSafeGetWithZero('thresholdPercent', summary);

	const summaryDetail = useMemo(
		() => ({
			'Full charge': formatPounds(nullSafeGetWithZero('fullChargeLb', summary)),
			'Added, 12 mo': formatPounds(nullSafeGetWithZero('addedLbTrailing12Mo', summary)),
			Recovered: formatPounds(nullSafeGetWithZero('recoveredLbTrailing12Mo', summary)),
			Refrigerant: nullSafeGetOrElse('refrigerantTypeCode', summary, '--'),
			// One cell, two numbers, as the design prints it: the GWP is the multiplier and the CO₂e is
			// what it produced, so splitting them across two columns separates a figure from the
			// constant that explains it.
			'GWP / CO₂e': formatGwpAndCo2e(
				nullSafeGetWithZero('gwp', summary),
				nullSafeGetWithZero('co2eTonnes', summary)
			),
			// Before the inspection date, deliberately: a repair deadline is a STATUTORY clock that is
			// either running or not, while the next inspection is a schedule. When both are present the
			// repair is the one with a consequence, so it reads first.
			//
			// `--` when there is no open obligation rather than being dropped from the panel — the summary
			// clears `repairDueAt` once a verified repair discharges the exceedance, and a cell that
			// vanishes is indistinguishable from a screen that never showed the field. The window it was
			// computed against sits beside it, because 30 days and the industrial-process 120 are
			// different clocks and the reader cannot tell which they are looking at from the date alone.
			'Leak Repair Due': nullSafeGet('repairDueAt', summary)
				? [
						dateFormatter(nullSafeGet('repairDueAt', summary), DATE_ONLY_FORMAT),
						nullSafeGet('repairWindowDays', summary)
							? `(${nullSafeGet('repairWindowDays', summary)}-day window)`
							: null,
				  ]
						.filter(Boolean)
						.join(' ')
				: '--',
			'Next Inspection': nullSafeGet('nextInspectionAt', summary)
				? dateFormatter(nullSafeGet('nextInspectionAt', summary), DATE_ONLY_FORMAT)
				: '--',
		}),
		[summary]
	);

	/**
	 * The identity panel beside the ledger. Read off the ASSET rather than the summary — these are
	 * facts about the equipment, not derived compliance numbers, and the summary deliberately carries
	 * only the latter.
	 */
	const assetIdentity = useMemo(
		() => [
			{ label: 'Asset ID', value: nullSafeGet('id', asset) },
			{ label: 'Asset Number', value: nullSafeGet('assetNumber', asset) },
			{ label: 'Manufacturer', value: nullSafeGet('manufacturer', asset) },
			{ label: 'Serial Number', value: nullSafeGet('serialNumber', asset) },
		],
		[asset]
	);

	const onOpenLogEvent = useCallback(() => setLogEventVisible(true), []);

	const onLogEventSuccess = useCallback(() => {
		setLogEventVisible(false);
		refresh();
	}, [refresh]);

	const isTracked = nullSafeGetOrElse('isRefrigerantTracked', summary, false);

	// Error BEFORE not-enrolled, and not-enrolled before the card. The order is the whole point: a
	// failed read must never be reported as "not in the programme", and a missing summary must never be
	// reported as "enrolled, nothing logged".
	if (loadFailed) {
		return (
			<div className="flex flex-col p-4">
				<Card>
					<EmptyState
						graphic={null}
						headline={'We could not load this appliance’s refrigerant record.'}
						body={
							<div className="mx-auto max-w-[440px] text-center">
								<div className="mb-4">
									Nothing is missing from the ledger — the request failed. Reload to try again.
								</div>
								<Button type="primary" onClick={refresh}>
									Retry
								</Button>
							</div>
						}
					/>
				</Card>
			</div>
		);
	}

	// `summary` is deliberately NOT required here. `retrieveRefrigerantAssetSummary` resolves null for a
	// 404 or an error envelope, which is exactly what an untracked (or not-visible) appliance returns —
	// so gating on `summary &&` meant those appliances rendered the full leak-rate card with every
	// figure blank instead of this empty state. Reachable by direct URL, and for a third-party supplier.
	if (!loading && !isTracked) {
		return (
			<div className="flex flex-col p-4">
				<Card>
					<EmptyState
						graphic={null}
						headline={'This appliance is not in the refrigerant programme.'}
						body={
							<div className="mx-auto max-w-[440px] text-center">
								Turn on "Uses Refrigerant" when editing the asset, and record its full charge,
								refrigerant and EPA appliance category — those three are what the leak rate and its
								threshold are computed from.
							</div>
						}
					/>
				</Card>
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-4 p-4">
			<Card
				title="Refrigerant details · 12 month leak rate"
				loading={loading && !summary}
				extra={
					<div className="flex flex-row items-center gap-3">
						{nullSafeGet('verificationDueAt', summary) ? (
							// A non-null verification due date IS the open obligation — the server clears the
							// field once a verification test satisfies it.
							<span style={{ color: 'rgb(213, 125, 5)' }}>
								Verification test due{' '}
								{dateFormatter(nullSafeGet('verificationDueAt', summary), DATE_ONLY_FORMAT)}
							</span>
						) : null}
						{canReadLedger ? (
							// Wrapped rather than a bare path so the record can find its way back HERE, with
							// whatever brought the user here still attached — the Refrigerant Tracking index
							// puts its filters and its "Back to Refrigerant Tracking" label in the query, and a
							// plain link to the record dropped them. The label names where this link is
							// standing, which is a work order when the tab is embedded in one.
							<Link
								to={getLinkWIthBackLinkParams(
									location,
									workOrder ? 'Back to Work Order' : 'Back to Asset',
									`/${userType}/assets/${assetId}/servicingRecord`
								)}
							>
								<Button>Servicing Record</Button>
							</Link>
						) : null}
					</div>
				}
			>
				<div className="mb-4 flex flex-row flex-wrap items-center gap-3">
					<RefrigerantBandDisplay band={nullSafeGet('complianceBand', summary)} />
					<span className="h4medium">
						{thresholdPercent === null || thresholdPercent === undefined
							? formatLeakRatePercent(leakRatePercent)
							: `${formatLeakRatePercent(leakRatePercent)} / ${thresholdPercent}%`}
					</span>
					{nullSafeGet('applianceCategory', summary) ? (
						<span className="footnoteregular text-character-secondary">
							{getApplianceCategoryLabel(nullSafeGet('applianceCategory', summary))}
						</span>
					) : null}
				</div>
				<div className="mb-4 max-w-[520px]">
					<RefrigerantLeakRateBar
						leakRatePercent={leakRatePercent}
						thresholdPercent={thresholdPercent}
						band={nullSafeGet('complianceBand', summary)}
						height={12}
					/>
				</div>
				<HorizontalKeyValueDisplay keyValueStore={summaryDetail} rowStyle={{ minWidth: '16%' }} />
			</Card>

			{/* The ledger and the appliance's identity sit side by side, per the design. `items-start`
			    keeps the identity panel from stretching to the ledger's height — an appliance with 40
			    events would otherwise leave a column of empty card below four short facts. It wraps
			    rather than shrinks below ~1100px, because neither column reads at half width. */}
			<div className="flex flex-row flex-wrap items-start gap-4">
				<div style={{ flex: '1 1 640px', minWidth: 0 }}>
					<Card
						title="Refrigerant ledger"
						loading={loading && ledger.length === 0}
						extra={
							// Same gate as the servicing record, for the write side. A third-party supplier's
							// POST is refused outright by the server (it requires a privateBuyerCompanyId), so
							// an enabled button here only lets them fill the whole drawer and then fail.
							canLogEvent ? (
								<AccessPermissionChecker name={PERMISSION_NAMES.MODIFY_ASSETS}>
									{/* Disabled until the summary is KNOWN, which is not the same as "not loading".
									    The drawer seeds its Refrigerant field from `initialValues`, which antd
									    reads ONCE at mount, so a drawer opened before the first summary lands
									    leaves that field blank on exactly the appliances the seed exists for —
									    the ones that inherit their refrigerant from their asset type and so carry
									    no code of their own. Nothing re-seeds it afterwards.
									    `loading && !summary`, not `loading`: `refresh()` raises the flag again on
									    every re-read, including the one fired straight after an event is saved,
									    and greying the button out there gates nothing — the summary it is waiting
									    for is already on screen. */}
									<OWButtonWithSupplierManagedLock
										disabled={loading && !summary}
										onClick={onOpenLogEvent}
									>
										Log Event
									</OWButtonWithSupplierManagedLock>
								</AccessPermissionChecker>
							) : null
						}
					>
						{ledger.length > 0 ? (
							<Table
								dataSource={ledger}
								columns={columns}
								rowKey={(record: any) => record.id}
								pagination={false}
								// A number, not `max-content`: the latter sized the table to the longest note on
								// one unwrapped line. See getRefrigerantEventLedgerMinTableWidth.
								scroll={ledgerScroll}
							/>
						) : (
							<EmptyState
								graphic={null}
								headline={'No servicing recorded yet.'}
								body={
									<div className="mx-auto max-w-[440px] text-center">
										<div className="mb-4">
											Every charge, recovery, repair, inspection and verification test on this
											appliance appears here — corrections included, because the ledger is
											append-only.
										</div>
										{/* Locked for the same reason the ledger header's Log Event is: a buyer whose
										    company is supplier-managed must not reach the write path, and an
										    empty-state action that stayed a plain Button was a way around it. */}
										{canLogEvent ? (
											<AccessPermissionChecker name={PERMISSION_NAMES.MODIFY_ASSETS}>
												{/* Same gate as the ledger header's button — see the note there. Unreachable
												    in practice: this branch sits in the Card BODY, which antd replaces with
												    a skeleton while the Card is loading, so the button is never on screen
												    with the flag raised. Kept so the two openers cannot disagree if the
												    Card's own loading condition is ever changed. */}
												<OWButtonWithSupplierManagedLock
													type="primary"
													disabled={loading && !summary}
													onClick={onOpenLogEvent}
												>
													Log the first event
												</OWButtonWithSupplierManagedLock>
											</AccessPermissionChecker>
										) : null}
									</div>
								}
							/>
						)}
					</Card>
				</div>

				<div style={{ flex: '0 1 300px', minWidth: 240 }}>
					<Card bodyStyle={{ padding: 0 }}>
						{assetIdentity.map((field) => (
							// `border-0 border-b border-solid` — Tailwind preflight is disabled in this repo, so a
							// width utility alone sets no border-style and the divider would not render at all.
							<div
								key={field.label}
								className="flex flex-col border-0 border-b border-solid border-grey-4 px-6 py-4"
							>
								{/* Value above label, as the design lays it out — the number is what the reader is
								    scanning for and the label only qualifies it. */}
								<span className="h5medium text-character-primary">
									{field.value === null || field.value === undefined || field.value === ''
										? '--'
										: field.value}
								</span>
								<span className="footnoteregular text-character-secondary">{field.label}</span>
							</div>
						))}
					</Card>
				</div>
			</div>

			{logEventVisible ? (
				<LogRefrigerantEventDrawer
					visible
					asset={asset}
					// Absent on the asset detail tab, so the drawer keeps its work-order picker there.
					workOrder={workOrder}
					refrigerantSummary={summary}
					onCancel={() => setLogEventVisible(false)}
					onSuccess={onLogEventSuccess}
				/>
			) : null}
		</div>
	);
};

const mapStateToProps = (state, ownProps) => ({
	// An embedding parent hands the appliance over directly (it already has it hydrated on the work
	// order); the asset detail route resolves it out of the store by route id.
	asset:
		ownProps.asset ||
		getEntityById(ownProps.assetId || nullSafeGet('match.params.id', ownProps), state.assets),
	events: state.refrigerant_events,
	currentUser: state.session.currentUser,
});

/**
 * `(dispatch)` ONLY — the role is taken as an argument rather than read off `ownProps`, and that is
 * load-bearing rather than stylistic.
 *
 * react-redux decides whether to recompute the dispatch props from this function's ARITY: declare
 * `ownProps` and it re-runs on every ownProps change, handing the component brand-new `fetchSummary`
 * and `fetchEvents` identities each time. This page's read effect depends on those identities, and
 * its own ownProps churn on every render of the asset detail page above it — `RouteWithSubRoutes`
 * builds a fresh `parentRoutes` array on each render, and `getEntityById` returns a fresh `{}` for an
 * asset that is not in the store yet, so that page re-renders on every store change while its asset
 * is still loading. The result was a cycle that fed itself: fetch → store change → re-render → new
 * dispatch props → fetch, which is the infinite `asset_summary` / `refrigerant_events` storm this
 * page issued when opened cold on its own URL.
 *
 * With no `ownProps` parameter these are computed once and never change, so the effect below re-runs
 * on the appliance changing and on nothing else.
 */
const mapDispatchToProps = (dispatch) => ({
	fetchSummary: (userType, assetId) => dispatch(retrieveRefrigerantAssetSummary(userType)(assetId)),
	fetchEvents: (userType, assetId) =>
		dispatch(
			refrigerantEventsRestCrudThunks(userType).read(
				{ assetId },
				REFRIGERANT_ASSET_LEDGER_TC,
				{ current: 1, pageSize: LEDGER_PAGE_SIZE },
				{ sort_by: 'occurredAt', order: 'descend' }
			)
		),
});

export default withUserType(
	withRouter<any, any>(
		connect(mapStateToProps, mapDispatchToProps)(AssetRefrigerantTrackingPage as any)
	) as any
);
